import { useContext, useMemo } from 'react'

import { MobileBottomNavigation, theme } from '@hhgtech/hhg-components'
import { LOCALE_HAS_COMMUNITY_SITE } from '@hhgtech/hhg-components/constants'
import { Scale, Community } from '@hhgtech/icons/other'
import { useRouter } from 'next/router'
import { useIntl } from 'react-intl'

import BabyGrowth from 'components/atoms/icons/BabyGrowth'
import KickCounter from 'components/atoms/icons/KickCounter'
import WeightTracker from 'components/atoms/icons/WeightTracker'
import RecalculationIcon from 'components/pages/healthTools/due-date/component/recalculationIcon'
import { useDueDateStore } from 'components/templates/dueDateCalculator/utils'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useSpacingSubotStickyBar } from 'hooks/useSpacingSubotStickyBar'
import { LOCALE } from 'interfaces/types'
import { AppContext } from 'state/appContext'

import { CommonStyle } from './commonStyles'
import { StyledMobileBottomNavigation } from './index.styled'
import { SharePopupNew } from '../sharePopupNew'

const MobileBottomFitnessHealthToolNavigation = ({
  isResult = false,
  topicLink,
  shareUrl,
  appTool = 'weight-tracker',
  isDueDateRecalculate,
}: {
  shareUrl?: string
  isResult?: boolean
  topicLink?: string
  appTool?: 'weight-tracker' | 'baby-kick-counter' | 'baby-growth'
  isDueDateRecalculate?: boolean
}) => {
  const { locale = 'vi-VN', pathname } = useRouter()
  const { formatMessage: f } = useIntl()
  const {
    state: { currentDomain },
  } = useContext(AppContext)
  useSpacingSubotStickyBar()
  const currentUrl = useCurrentUrl()
  const haveCommunity = useMemo(() => {
    return LOCALE_HAS_COMMUNITY_SITE.includes(locale as LOCALE)
  }, [locale])
  const { setIsRecalculate } = useDueDateStore()

  const renderedTools = () => {
    if (isDueDateRecalculate) {
      return (
        <MobileBottomNavigation.Icon
          icon={<RecalculationIcon color={theme.colors.gray600} size={22} />}
          label={f({
            id: 'tools.recalculate',
            defaultMessage: 'Recalculate',
          })}
          onClick={() => {
            setIsRecalculate(true)
            document.body.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }}
          data-event-category="Health Tool"
          data-event-action="Due Date - Result Page - Sticky Bottom"
          data-event-label="Re-calculate"
          className="pointer-event-child-none"
        />
      )
    }

    if (locale === LOCALE.Vietnam) {
      return appTool === 'weight-tracker' ? (
        <a
          href={`https://applink.hellobacsi.com/BBdn6JC3vLb?utm_source=healthtools&utm_medium=hellobacsi&utm_campaign=app install_Web_mobile bottom bar_${currentUrl}`}
          className="nav-item pointer-event-child-none"
          data-event-category="Health Tool"
          data-event-action="Weight Tracker - Sticky Bottom"
          data-event-label={currentUrl}
        >
          <MobileBottomNavigation.Icon
            icon={<WeightTracker />}
            label={f({
              id: 'mobileBar.weightTracker',
              defaultMessage: 'Weight Tracker',
            })}
          />
        </a>
      ) : appTool === 'baby-kick-counter' ? (
        <a
          href={`https://applink.hellobacsi.com/fGZIKhbB6Lb`}
          className="nav-item pointer-event-child-none"
          data-event-category="Health Tool"
          data-event-action="Baby Kick Counter - Sticky Bottom"
          data-event-label={currentUrl}
        >
          <MobileBottomNavigation.Icon
            icon={<KickCounter />}
            label={f({
              id: 'mobileBar.weightTracker',
              defaultMessage: 'Weight Tracker',
            })}
          />
        </a>
      ) : appTool === 'baby-growth' ? (
        <a
          href={`https://hellobacsivn.page.link/app`}
          className="nav-item pointer-event-child-none"
          data-event-category="Health Tool"
          data-event-action="Home Mobile App - Sticky Bottom"
          data-event-label={currentUrl}
        >
          <MobileBottomNavigation.Icon
            icon={<BabyGrowth />}
            label={f({
              id: 'mobileBar.otherBabyTool',
              defaultMessage: 'Weight Tracker',
            })}
          />
        </a>
      ) : null
    }

    if (haveCommunity) {
      return (
        <a
          href={
            isResult ? currentDomain + topicLink : currentDomain + '/community'
          }
          className="nav-item pointer-event-child-none"
          data-event-category="Sticky Bottom"
          data-event-action="Together Click"
          data-event-label={
            isResult ? currentDomain + topicLink : currentDomain + '/community'
          }
        >
          <MobileBottomNavigation.Icon
            icon={
              isResult ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M2.8 21.175L7.175 8.9L15.075 16.8L2.8 21.175ZM5.275 18.7L12.325 16.2L7.775 11.65L5.275 18.7ZM13.975 12.7L13.15 11.875L18.625 6.375C19.125 5.875 19.7333 5.625 20.45 5.625C21.1667 5.625 21.775 5.875 22.275 6.375L22.75 6.875L21.925 7.7L21.45 7.225C21.1833 6.94167 20.854 6.8 20.462 6.8C20.0707 6.8 19.7417 6.94167 19.475 7.225L13.975 12.7ZM10.325 9.05L9.5 8.2L10.1 7.6C10.3833 7.31667 10.525 6.96233 10.525 6.537C10.525 6.11233 10.3833 5.75 10.1 5.45L9.45 4.825L10.3 3.975L10.925 4.6C11.4583 5.13333 11.725 5.775 11.725 6.525C11.725 7.275 11.4583 7.91667 10.925 8.45L10.325 9.05ZM12.15 10.875L11.325 10.05L14.8 6.55C15.0667 6.28333 15.2 5.954 15.2 5.562C15.2 5.17067 15.0667 4.84167 14.8 4.575L13.325 3.1L14.15 2.25L15.65 3.725C16.15 4.24167 16.4 4.854 16.4 5.562C16.4 6.27067 16.15 6.88333 15.65 7.4L12.15 10.875ZM15.8 14.525L14.975 13.7L16.175 12.5C16.7083 11.95 17.3583 11.675 18.125 11.675C18.8917 11.675 19.5417 11.95 20.075 12.5L21.275 13.7L20.425 14.525L19.225 13.325C18.925 13.025 18.5543 12.875 18.113 12.875C17.671 12.875 17.3 13.025 17 13.325L15.8 14.525Z"
                    fill="#595959"
                  />
                </svg>
              ) : (
                <Community />
              )
            }
            label={f({
              id: 'mobileBar.community',
              defaultMessage: 'Community',
            })}
          />
        </a>
      )
    }

    return (
      <a
        href={currentDomain}
        className="nav-item pointer-event-child-none"
        data-event-category="Sticky Bottom"
        data-event-action="Home Click"
        data-event-label={currentDomain + '/categories'}
      >
        <MobileBottomNavigation.Icon
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M8.8 10.6248C8.45 10.6248 8.18733 10.4665 8.012 10.1498C7.83733 9.83314 7.84167 9.5248 8.025 9.2248L11.225 3.9998C11.4083 3.6998 11.6667 3.5498 12 3.5498C12.3333 3.5498 12.5917 3.6998 12.775 3.9998L15.975 9.2248C16.1583 9.5248 16.1627 9.83314 15.988 10.1498C15.8127 10.4665 15.55 10.6248 15.2 10.6248L8.8 10.6248ZM17.5 21.6248C16.35 21.6248 15.375 21.2248 14.575 20.4248C13.775 19.6248 13.375 18.6498 13.375 17.4998C13.375 16.3498 13.775 15.3748 14.575 14.5748C15.375 13.7748 16.35 13.3748 17.5 13.3748C18.65 13.3748 19.625 13.7748 20.425 14.5748C21.225 15.3748 21.625 16.3498 21.625 17.4998C21.625 18.6498 21.225 19.6248 20.425 20.4248C19.625 21.2248 18.65 21.6248 17.5 21.6248ZM4.275 21.1248C4.025 21.1248 3.81267 21.0375 3.638 20.8628C3.46267 20.6875 3.375 20.4665 3.375 20.1998L3.375 14.7748C3.375 14.5248 3.46267 14.3121 3.638 14.1368C3.81267 13.9621 4.03333 13.8748 4.3 13.8748H9.725C9.975 13.8748 10.1877 13.9621 10.363 14.1368C10.5377 14.3121 10.625 14.5331 10.625 14.7998V20.2248C10.625 20.4748 10.5377 20.6875 10.363 20.8628C10.1877 21.0375 9.96667 21.1248 9.7 21.1248H4.275ZM17.5 20.1248C18.2333 20.1248 18.8543 19.8708 19.363 19.3628C19.871 18.8541 20.125 18.2331 20.125 17.4998C20.125 16.7665 19.871 16.1455 19.363 15.6368C18.8543 15.1288 18.2333 14.8748 17.5 14.8748C16.7667 14.8748 16.146 15.1288 15.638 15.6368C15.1293 16.1455 14.875 16.7665 14.875 17.4998C14.875 18.2331 15.1293 18.8541 15.638 19.3628C16.146 19.8708 16.7667 20.1248 17.5 20.1248ZM4.875 19.6248H9.125V15.3748H4.875V19.6248ZM9.85 9.1248H14.15L12 5.6498L9.85 9.1248Z"
                fill="#595959"
              />
            </svg>
          }
          label={f({
            id: 'mobileBar.home',
            defaultMessage: 'Home',
          })}
          isActive={pathname === '/'}
        />
      </a>
    )
  }

  return (
    <>
      <StyledMobileBottomNavigation isCollapsed={false} data-has-share={true}>
        <a
          href={currentDomain + '/health-tools'}
          className="nav-item pointer-event-child-none"
          data-event-category="Sticky Bottom"
          data-event-action="Engage Click"
          data-event-label={currentDomain + '/health-tools'}
        >
          <MobileBottomNavigation.Icon
            icon={<Scale isActive={true} />}
            label={f({
              id: 'mobileBar.healthTools',
              defaultMessage: 'Health Tools',
            })}
            isActive={true}
          />
        </a>

        {renderedTools()}
        <SharePopupNew.BottomNavHorizonal
          shareUrl={shareUrl || currentUrl}
          hasOverlay
          copyInputProps={{
            popoverProps: {
              position: 'top',
              withinPortal: true,
            },
          }}
        />
      </StyledMobileBottomNavigation>
      <style>{CommonStyle}</style>
    </>
  )
}

export default MobileBottomFitnessHealthToolNavigation
